import { motion } from "framer-motion";
import { MessagesSquare, Timer, Users, KeyRound, Landmark, Quote, Star } from "lucide-react";
import { useLang } from "@/i18n";

const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1525541616755-4f56dd0a39d2?fm=jpg&q=85&w=1600&auto=format&fit=crop";

const reveal = {
  hidden: { opacity: 0, y: 48 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Chapter = ({ id, number, eyebrow, children, testid }) => (
  <section id={id} data-testid={testid} className="border-b border-white/10">
    <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-24 sm:py-32 lg:py-40">
      <div className="grid grid-cols-12 gap-6 lg:gap-10">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="col-span-12 lg:col-span-4"
        >
          <span className="font-display text-[26vw] lg:text-[11vw] leading-[0.8] text-outline-copper block select-none">
            {number}
          </span>
          <p className="mt-6 text-xs tracking-[0.35em] uppercase text-copper">{eyebrow}</p>
        </motion.div>
        <div className="col-span-12 lg:col-span-8 lg:pt-[6vw]">{children}</div>
      </div>
    </div>
  </section>
);

export default function Chapters() {
  const { t } = useLang();

  const services = [
    { icon: KeyRound, num: "02.1", title: t.ch2.card1t, body: t.ch2.card1b, testid: "service-real-estate-card" },
    { icon: Landmark, num: "02.2", title: t.ch2.card2t, body: t.ch2.card2b, testid: "service-mortgage-card" },
  ];

  const values = [
    { icon: MessagesSquare, title: t.ch4.row1t, body: t.ch4.row1b, testid: "value-communication-row" },
    { icon: Timer, title: t.ch4.row2t, body: t.ch4.row2b, testid: "value-timeline-row" },
    { icon: Users, title: t.ch4.row3t, body: t.ch4.row3b, testid: "value-team-row" },
  ];

  const team = [
    { name: "Omar Castro", role: t.ch5.omarRole, phone: "602-748-7239", email: "ocastro@pricemortgage.com", img: "/images/omar.jpg", testid: "team-omar-card" },
    { name: "Paula Castro", role: t.ch5.paulaRole, phone: "(602) 373-2882", email: "paulacastroazrealtor@gmail.com", img: "/images/paula.jpg", testid: "team-paula-card" },
  ];

  return (
    <>
      <Chapter id="about" number="01" eyebrow={t.ch1.eyebrow} testid="chapter-about">
        <motion.h2
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl"
        >
          {t.ch1.h2a} <span className="italic text-copper">{t.ch1.h2b}</span>
        </motion.h2>
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="mt-10 max-w-xl text-base leading-relaxed text-bone/60"
        >
          {t.ch1.body}
        </motion.p>
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="mt-14 group"
        >
          <div className="overflow-hidden border border-white/10">
            <img
              src={INTERIOR_IMG}
              alt={t.ch1.alt}
              className="w-full h-[320px] sm:h-[420px] object-cover scale-105 group-hover:scale-100 transition-transform duration-[1200ms] ease-out"
            />
          </div>
          <p className="mt-4 text-[10px] tracking-[0.3em] uppercase text-bone/30">
            {t.ch1.caption}
          </p>
        </motion.div>
      </Chapter>

      <Chapter id="services" number="02" eyebrow={t.ch2.eyebrow} testid="chapter-services">
        <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {services.map((s, i) => (
            <motion.div
              key={s.num}
              data-testid={s.testid}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-10%" }}
              transition={{ delay: i * 0.12 }}
              className="group bg-ink p-10 sm:p-14 hover:bg-surface transition-colors duration-500"
            >
              <div className="flex items-start justify-between">
                <s.icon size={28} strokeWidth={1.25} className="text-copper" />
                <span className="font-display italic text-bone/25 text-lg">{s.num}</span>
              </div>
              <h3 className="mt-16 font-display text-2xl sm:text-3xl tracking-tight leading-tight group-hover:text-copper transition-colors duration-500">
                {s.title}
              </h3>
              <p className="mt-6 text-sm leading-relaxed text-bone/50">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </Chapter>

      <Chapter id="advantage" number="03" eyebrow={t.ch3.eyebrow} testid="chapter-advantage">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="flex flex-wrap items-end gap-x-8 gap-y-4"
        >
          <span data-testid="advantage-stat" className="font-display text-[28vw] sm:text-[16vw] lg:text-[13vw] leading-[0.8] text-copper">
            50+
          </span>
          <span className="font-display italic text-3xl sm:text-4xl text-bone/80 pb-4 max-w-[12ch] leading-tight">
            {t.ch3.label}
          </span>
        </motion.div>
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="mt-12 max-w-xl text-base leading-relaxed text-bone/60"
        >
          {t.ch3.body}
        </motion.p>
      </Chapter>

      <Chapter id="value" number="04" eyebrow={t.ch4.eyebrow} testid="chapter-value">
        <motion.h2
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl"
        >
          {t.ch4.h2a} <span className="italic text-copper">{t.ch4.h2b}</span>
        </motion.h2>
        <div className="mt-14 divide-y divide-white/10 border-y border-white/10">
          {values.map((v, i) => (
            <motion.div
              key={v.testid}
              data-testid={v.testid}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-10%" }}
              transition={{ delay: i * 0.1 }}
              className="group grid sm:grid-cols-12 gap-4 sm:gap-8 items-center py-10 sm:py-12"
            >
              <div className="sm:col-span-1">
                <v.icon size={24} strokeWidth={1.25} className="text-copper" />
              </div>
              <h3 className="sm:col-span-5 font-display text-2xl sm:text-3xl tracking-tight group-hover:text-copper group-hover:translate-x-2 transition-all duration-500">
                {v.title}
              </h3>
              <p className="sm:col-span-6 text-sm leading-relaxed text-bone/50">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </Chapter>

      <Chapter id="testimonials" number="05" eyebrow={t.testi.eyebrow} testid="chapter-testimonials">
        <motion.h2
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl"
        >
          {t.testi.h2a} <span className="italic text-copper">{t.testi.h2b}</span>
        </motion.h2>
        <div className="mt-14 grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {t.testi.quotes.map((q, i) => (
            <motion.figure
              key={q.name}
              data-testid={`testimonial-${i}`}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-10%" }}
              transition={{ delay: i * 0.12 }}
              className="group bg-ink p-10 sm:p-12 flex flex-col justify-between hover:bg-surface transition-colors duration-500"
            >
              <div>
                <Quote size={26} strokeWidth={1.25} className="text-copper" />
                <blockquote className="mt-8 font-display italic text-xl sm:text-2xl leading-snug text-bone/85">
                  &ldquo;{q.text}&rdquo;
                </blockquote>
              </div>
              <figcaption className="mt-10">
                <div className="flex gap-1 mb-4" aria-label="5 out of 5 stars">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={12} className="fill-copper text-copper" />
                  ))}
                </div>
                <p className="text-sm text-bone/90">{q.name}</p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-copper mt-1.5">{q.detail}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </Chapter>

      <Chapter id="team" number="06" eyebrow={t.ch5.eyebrow} testid="chapter-team">
        <motion.h2
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl"
        >
          {t.ch5.h2a} <span className="italic text-copper">{t.ch5.h2b}</span>
        </motion.h2>
        <div className="mt-14 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {team.map((m, i) => (
            <motion.div
              key={m.testid}
              data-testid={m.testid}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-10%" }}
              transition={{ delay: i * 0.12 }}
              className="group bg-ink"
            >
              <div className="overflow-hidden">
                <img
                  src={m.img}
                  alt={`${m.name}, ${m.role} at OPEAM Group`}
                  className="w-full aspect-[4/5] object-cover object-top scale-105 group-hover:scale-100 transition-transform duration-[1200ms] ease-out"
                />
              </div>
              <div className="p-8 sm:p-10">
                <h3 className="font-display text-3xl tracking-tight group-hover:text-copper transition-colors duration-500">
                  {m.name}
                </h3>
                <p className="mt-2 text-[10px] tracking-[0.3em] uppercase text-copper">{m.role}</p>
                <div className="mt-5 space-y-1.5 text-sm text-bone/50">
                  <a href={`tel:${m.phone.replace(/\D/g, "")}`} className="block hover:text-copper transition-colors duration-300 w-fit">
                    {m.phone}
                  </a>
                  <a href={`mailto:${m.email}`} className="block hover:text-copper transition-colors duration-300 w-fit break-all">
                    {m.email}
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Chapter>
    </>
  );
}
